import api from './api';
import { NutritionOnloadApiResponse } from '@/components/Nutrition/types/nutritionOnload.types';
import { 
    GetNutritionApiResponse,
    GetAndUploadLabelTemplateRequest,
    GetAndUploadLabelTemplateResponse
} from '@/types/NutritionApi.types';
import { 
    NutrientListingApiResponse, 
    SaveNutrientRequest, 
    SaveNutrientResponse,
    SaveNutritionRequest,
    SaveNutritionResponse,
    GetNutritionSectionsResponse,
    GetNutrientResponse,
    CreateNutritionTabRequest,
    CreateNutritionTabResponse
} from '@/types/Nutrition.types';

const { aggregatedServiceAPI, pimcoreServiceAPI } = api;

const NutrientService = {
    /**
     * Fetch nutrition onload data including dropdowns and nutrient lists
     * @returns Promise with nutrition onload API response
     */
    getNutritionOnload: async (): Promise<NutritionOnloadApiResponse> => {
        try {
            const response = await aggregatedServiceAPI.get('ProductVendorNutrition/onload');
            return response.data;
        } catch (error) {
            console.error("Error fetching nutrition onload data:", error);
            throw error;
        }
    },

    /**
     * Fetch pet food nutrients onload data for add/edit nutrient dialog
     * @returns Promise with pet food nutrients onload API response
     */
    getPetFoodNutrientsOnload: async (): Promise<any[]> => {
        try {
            const response = await aggregatedServiceAPI.get('ProductVendorNutrition/petFoodNutrients/onload');
            return response.data;
        } catch (error) {
            console.error("Error fetching pet food nutrients onload data:", error);
            throw error;
        }
    },

    // /**
    //  * Fetch complete nutrition data for a product specification
    //  * @param productSpecificationId - The product specification ID
    //  * @param assessmentId - Required assessment ID
    //  * @returns Promise with nutrition data API response
    //  */
    // getNutrition: async (
    //     productSpecificationId: string | number,
    //     assessmentId: string
    // ): Promise<GetNutritionApiResponse> => {
    //     try {
    //         const url = `ProductVendorNutrition/getNutritionListing/${productSpecificationId}?assessmentId=${assessmentId}`;
    //         
    //         const response = await aggregatedServiceAPI.get(url);
    //   
    //         // if (response.data) {
    //         //     response.data.packCopy = {
    //         //         id: "4940",
    //         //         filename: "Pack_Copy_Test_M_SKU_99848484_Test_M_20251104_084419.docx"
    //         //     };
    //         // }
    //         
    //         return response.data;
    //     } catch (error) {
    //         console.error("Error fetching nutrition data:", error);
    //         throw error;
    //     }
    // },

    /**
     * Fetch nutrient listing for a specific product specification
     * Endpoint: GET /ProductVendorNutrition/getNutrientListing/{productSpecificationId}
     * @param productSpecificationId - The product specification ID
     * @returns Promise with nutrient listing API response
     */
    getNutrientListData: async (productSpecificationId: string | number): Promise<NutrientListingApiResponse> => {
        try {
            const response = await aggregatedServiceAPI.get(
                `ProductVendorNutrition/getNutrientListing/${productSpecificationId}`
            );
            return response.data;
        } catch (error) {
            console.error("Error fetching nutrient list data:", error);
            throw error;
        }
    },

    /**
     * Save or update a single nutrient
     * POST /ProductVendorNutrition/saveNutrient
     * @param nutrientData - Nutrient data to save (includes id for updates)
     * @returns Promise with saved nutrient ID
     */
    saveNutrient: async (nutrientData: SaveNutrientRequest): Promise<SaveNutrientResponse> => {
        try {
            console.log(' [NutrientService] Saving nutrient:', nutrientData);
            const response = await aggregatedServiceAPI.post(
                'ProductVendorNutrition/saveNutrient',
                nutrientData
            );
            console.log(' [NutrientService] Nutrient saved successfully:', response.data);
            return response.data;
        } catch (error) {
            console.error(" [NutrientService] Error saving nutrient:", error);
            throw error;
        }
    },

    /**
     * Save complete nutrition form data
     * POST /ProductVendorNutrition/saveNutrition?vendorAssessmentId={vendorAssessmentId}
     * Used for workflow save/next actions and share for review
     * @param nutritionData - Complete nutrition form data with optional transitionObject
     * @param vendorAssessmentId - Optional vendor assessment ID to include in URL parameter
     * @returns Promise with saved nutrition ID and transition status
     */
    saveNutrition: async (
        nutritionData: SaveNutritionRequest,
        vendorAssessmentId?: string | number
    ): Promise<SaveNutritionResponse> => {
        try {
            let url = 'ProductVendorNutrition/saveNutrition';
            if (vendorAssessmentId) {
                url += `?vendorAssessmentId=${vendorAssessmentId}`;
            }
            
            const response = await aggregatedServiceAPI.post(
                url,
                nutritionData
            );
            
            return response.data;
        } catch (error: any) {
            throw error;
        }
    },

    /**
     * Generate and upload NFP label template to file storage
     * POST /ProductVendorNutrition/getAndUploadLabelTemplate
     * @param payload - Label ID (genesisId) and nutrition tab ID
     * @returns Promise with asset ID and generation timestamp
     */
    getAndUploadLabelTemplate: async (
        payload: GetAndUploadLabelTemplateRequest
    ): Promise<GetAndUploadLabelTemplateResponse> => {
        try {
            console.log(' [NutrientService] Generating and uploading NFP label template:', payload);
            
            const response = await aggregatedServiceAPI.post(
                'ProductVendorNutrition/getAndUploadLabelTemplate',
                payload
            );
            
            console.log(' [NutrientService] NFP label template uploaded successfully:', response.data);
            return response.data;
        } catch (error) {
            console.error(" [NutrientService] Error generating/uploading NFP label:", error);
            throw error;
        }
    },

    /**
     * Delete a nutrient by its ID
     * DELETE /ProductVendorNutrition/deleteNutrient/{nutrientId}
     * @param nutrientId - The nutrient ID to delete
     * @returns Promise with deletion response
     */
    deleteNutrient: async (nutrientId: string | number): Promise<any> => {
        try {
            console.log(' [NutrientService] Deleting nutrient:', nutrientId);
            
            const response = await aggregatedServiceAPI.delete(
                `ProductVendorNutrition/deleteNutrient/${nutrientId}`
            );
            
            console.log(' [NutrientService] Nutrient deleted successfully:', response.data);
            return response.data;
        } catch (error) {
            console.error(" [NutrientService] Error deleting nutrient:", error);
            throw error;
        }
    },

    /**
     * Fetch nutrition sections for a specific assessment
     * GET /ProductVendorNutrition/sections/nutrition?assessmentId={assessmentId}
     * @param assessmentId - The assessment ID
     * @returns Promise with nutrition sections response containing nutritionTab array
     */
    getNutritionSections: async (
        assessmentId: string | number
    ): Promise<GetNutritionSectionsResponse> => {
        try {
            const url = `ProductVendorNutrition/sections/nutrition?assessmentId=${assessmentId}`;
            
            const response = await aggregatedServiceAPI.get(url);
            
            return response.data;
        } catch (error) {
            console.error(" [NutrientService] Error fetching nutrition sections:", error);
            throw error;
        }
    },

    /**
     * Fetch a single nutrient by its ID
     * GET /ProductVendorNutrition/getNutrient/{nutrientId}
     * @param nutrientId - The nutrient ID
     * @returns Promise with nutrient details
     */
    getNutrient: async (
        nutrientId: string | number
    ): Promise<GetNutrientResponse> => {
        try {
            const url = `ProductVendorNutrition/getNutrient/${nutrientId}`;
            
            const response = await aggregatedServiceAPI.get(url);
            
            return response.data;
        } catch (error) {
            console.error(" [NutrientService] Error fetching nutrient:", error);
            throw error;
        }
    },

    /**
     * Create a new nutrition tab
     * POST /shared/NutritionTab/createNutritionTab
     * @param payload - Nutrition tab creation data with section name and vendor assessment ID
     * @returns Promise with created nutrition tab details
     */
    createNutritionTab: async (
        payload: CreateNutritionTabRequest
    ): Promise<CreateNutritionTabResponse> => {
        try {
            const url = 'shared/NutritionTab/createNutritionTab';
            
            const response = await pimcoreServiceAPI.post(url, payload);
            
            return response.data;
        } catch (error) {
            console.error(" [NutrientService] Error creating nutrition tab:", error);
            throw error;
        }
    },

    /**
     * Delete a nutrition tab (section)
     * @param nutritionTabId - NutritionTab id to delete
     */
    deleteNutritionTab: async (nutritionTabId: string | number): Promise<any> => {
        if (nutritionTabId == null || String(nutritionTabId).trim() === '') {
            throw new Error('Cannot delete nutrition section: nutritionTabId is missing');
        }

        const idNum = Number(String(nutritionTabId).trim());
        if (!Number.isFinite(idNum)) {
            throw new Error('Cannot delete nutrition section: nutritionTabId is not a valid number');
        }

        try {
            // Confirmed endpoint:
            // DELETE /shared/NutritionTab/deleteNutritionTab  body: { id: number }
            const response = await pimcoreServiceAPI.post(
                'shared/NutritionTab/deleteNutritionTab',
                { id: idNum }
            );
            return response.data;
        } catch (error) {
            console.error(' [NutrientService] Error deleting nutrition tab:', { nutritionTabId: idNum, error });
            throw error;
        }
    }
};

export default NutrientService;
